import { Injectable } from '@angular/core';
import { TDashboardComponentDescriptor, TDashboardConfig, TWidgetLayout } from '@monorepo/types';

@Injectable({
  providedIn: 'root',
})
export class TvDashboardLayoutService {
  /**
   * initializes the dashboard layout with given rows, columns, and component descriptors.
   * arranges components in the grid and returns their configuration.
   * @param {number} rows - Number of rows in the dashboard grid.
   * @param {number} columns - Number of columns in the dashboard grid.
   * @param {TDashboardComponentDescriptor[]} dashboards - List of component descriptors containing widget details and dimensions.
   * @param {TDashboardConfig['header']['heightInVh']} dashboardHeaderWidgetHeight - Height of dashboard header widget in vh.
   * @returns {TWidgetLayout[]} Array of widget layout configurations with widget name, data, position, and dimensions.
   */
  initialiseDashboard(
    rows: number,
    columns: number,
    dashboards: TDashboardComponentDescriptor[],
    dashboardHeaderWidgetHeight: TDashboardConfig['header']['heightInVh'],
  ): TWidgetLayout[] {
    const componentsConfig: TWidgetLayout[] = [];
    const gridStatus: number[][] = Array(rows)
      .fill(0)
      .map(() => Array(columns).fill(0));

    dashboards.map((component) => {
      const position = this.findFirstEmptyPlace(gridStatus, rows, columns, component.size[1], component.size[0]);
      if (position) {
        this.updateGrid(gridStatus, position[0], position[1], component.size[1], component.size[0]);
        componentsConfig.push({
          widget: {
            ...component,
            sizeInViewport: {
              width: (100 * component.size[0]) / columns,
              height: ((100 - dashboardHeaderWidgetHeight) * component.size[1]) / rows,
            },
          },
          position: {
            left: position[1],
            top: position[0],
          },
          dimensions: {
            width: component.size[0],
            height: component.size[1],
          },
        });
      }
    });
    return componentsConfig;
  }

  /**
   * Finds the first available position in the grid where a widget can fit.
   * @param {number[][]} grid - The current grid layout status.
   * @param {number} rows - Number of rows in the grid.
   * @param {number} columns - Number of columns in the grid.
   * @param {number} widgetHeight - The widget's height in grid units.
   * @param {number} widgetWidth - The widget's width in grid units.
   * @returns {[number, number] | null} Coordinates [row, column] of the first empty place or null if none is found.
   */
  findFirstEmptyPlace(
    grid: number[][],
    rows: number,
    columns: number,
    widgetHeight: number,
    widgetWidth: number,
  ): [number, number] | null {
    for (let rowIndex = 0; rowIndex <= rows - widgetHeight; rowIndex++) {
      for (let columnIndex = 0; columnIndex <= columns - widgetWidth; columnIndex++) {
        if (this.canPlaceWidget(grid, rows, columns, rowIndex, columnIndex, widgetHeight, widgetWidth)) {
          return [rowIndex, columnIndex];
        }
      }
    }
    return null;
  }

  /**
   * Checks if a widget of specified dimensions can be placed at a given position in the grid.
   * @param {number[][]} grid - The current grid layout status.
   * @param {number} rows - Number of rows in the grid.
   * @param {number} columns - Number of columns in the grid.
   * @param {number} rowIndex - Starting row index for widget placement.
   * @param {number} columnIndex - Starting column index for widget placement.
   * @param {number} widgetHeight - The widget's height in grid units.
   * @param {number} widgetWidth - The widget's width in grid units.
   * @returns {boolean} True if the widget can be placed, otherwise false.
   */
  canPlaceWidget(
    grid: number[][],
    rows: number,
    columns: number,
    rowIndex: number,
    columnIndex: number,
    widgetHeight: number,
    widgetWidth: number,
  ): boolean {
    return (
      rowIndex + widgetHeight <= rows &&
      columnIndex + widgetWidth <= columns &&
      grid
        .slice(rowIndex, rowIndex + widgetHeight)
        .every((row) => row.slice(columnIndex, columnIndex + widgetWidth).every((cell) => cell === 0))
    );
  }

  /**
   * Updates the grid to mark cells as occupied for a placed widget at a given position.
   * @param {number[][]} grid - The current grid layout status.
   * @param {number} rowIndex - Starting row index for widget placement.
   * @param {number} columnIndex - Starting column index for widget placement.
   * @param {number} widgetHeight - The widget's height in grid units.
   * @param {number} widgetWidth - The widget's width in grid units.
   */
  updateGrid(grid: number[][], rowIndex: number, columnIndex: number, widgetHeight: number, widgetWidth: number) {
    // NOTE [kuldeep] using slice and foreach below instead of map. this is necessary to keep gridStatus as const
    grid.slice(rowIndex, rowIndex + widgetHeight).forEach((row) => {
      row.slice(columnIndex, columnIndex + widgetWidth).forEach((_, cIdx) => {
        row[columnIndex + cIdx] = 1;
      });
    });
  }
}
